import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Form, Input, Spin } from 'antd';
import { fetchUtmSettings } from '_utils/misc';
import { LoadingOutlined } from '@ant-design/icons';

type Props = {
  bigTitle?: string,
  title?: string,
  serviceRequired?: string,
  focus: boolean,
  fields?: string[],
  onDone: (token: string) => void,
  submitData: (data: Object) => void,
};

const CarValuationFirstStepForm = ({
  bigTitle,
  title,
  serviceRequired,
  focus,
  onDone,
  submitData,
}: Props) => {
  const [busy, setBusy] = useState(false);
  const firstFieldRef = useRef();

  const [form] = Form.useForm();

  useEffect(() => {
    if (focus) {
      form.validateFields();
      firstFieldRef.current.focus();
    }
  }, [focus]);

  const onFinish = (values) => {
    setBusy(true);
    submitData(values)
      .then(function (res) {
        if (res.data.result === 'Success') {
          onDone(res.data.token, values);
        }
      })
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSettings = fetchUtmSettings(params);
    form.setFieldsValue({
      utm_campaign: utmSettings.rCampaign || '',
      utm_medium: utmSettings.rMedium || '',
      utm_source: utmSettings.rSource || '',
      utm_term: utmSettings.rTerm || '',
      utm_content: utmSettings.rContent || '',
    });
  }, []);

  return (
    <Form
      className={'lead-page-form opacity-95 lg:opacity-100 transition-opacity'}
      onFinish={onFinish}
      initialValues={{
        // sample
        // vehicle_no: 'SJL3712C',
        // mobile_no: '91234567',
        // owner_name: 'Charlie Tan',

        // php_self: window.location.href,
        service_required: serviceRequired,
        is_for_valuation: serviceRequired === 'Valuation' ? 1 : '',
        privacy_agreed: false,
        utm_campaign: '',
        utm_medium: '',
        utm_source: '',
        utm_term: '',
        utm_content: '',
      }}
      form={form}
    >
      <Spin
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        spinning={busy}
      >
        {bigTitle ? (
          <h1 className="font-bold text-center lg:mt-0 mt-5 mb-5 color-black-100 leading-snug">
            {bigTitle}
          </h1>
        ) : null}
        {title ? (
          <h3 className="font-bold text-center mt-5 mb-5">{title}</h3>
        ) : null}
        <Form.Item
          name="vehicle_no"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
        >
          <Input
            ref={firstFieldRef}
            placeholder="Enter the car plate no."
            size="large"
          />
        </Form.Item>
        <Form.Item
          name="owner_name"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
        >
          <Input placeholder="Enter your full name" size="large" />
        </Form.Item>
        <Form.Item
          name="mobile_no"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
            {
              pattern: /^([689])\d{7}$/,
              message: 'Please enter a valid Singapore mobile number',
            },
          ]}
        >
          <Input placeholder="Enter your mobile" size="large" />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="font-bold get-quote-button"
            size="large"
          >
            Get Quote
          </Button>
        </Form.Item>
        <Form.Item
          name="privacy_agreed"
          valuePropName="checked"
          className="error-message-top"
          rules={[
            () => ({
              validator(_, value) {
                if (value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('You must agree terms and conditions.')
                );
              },
            }),
          ]}
        >
          <Checkbox>
            <p className="text-sm text-justify m-0">
              I agree to the collection, use and disclosure of my personal data
              by OneShift and/or such other third party (as may be necessary)
              and to be contacted (via email, phone call and/or text message)
              for any purposes in connection with the application of and
              transaction of the vehicle, in accordance with the Personal Data
              Protection Act 2012 and OneShift's{' '}
              <a href="/privacy" target="_blank">
                Privacy Policy
              </a>
              .
            </p>
          </Checkbox>
        </Form.Item>
        <div className="utm-settings">
          <Form.Item hidden name="referer">
            <input type="hidden" name="referer" id="referer" />
          </Form.Item>
          <Form.Item hidden name="php_self">
            <input type="hidden" />
          </Form.Item>
          <Form.Item hidden name="utm_campaign">
            <input type="hidden" />
          </Form.Item>
          <Form.Item hidden name="utm_medium">
            <input type="hidden" />
          </Form.Item>
          <Form.Item hidden name="utm_source">
            <input type="hidden" />
          </Form.Item>
          <Form.Item hidden name="utm_term">
            <input type="hidden" />
          </Form.Item>
          <Form.Item hidden name="utm_content">
            <input type="hidden" />
          </Form.Item>
          <Form.Item hidden name="is_for_valuation">
            <input type="hidden" />
          </Form.Item>
          <Form.Item hidden name="service_required">
            <input type="hidden" />
          </Form.Item>
        </div>
      </Spin>
    </Form>
  );
};

export default CarValuationFirstStepForm;
